import { authEvents } from '../auth/authEvents';
import { sessionManager } from '../auth/sessionManager';
import { HTTP_STATUS } from '../common/constants';
import {
  decrementPendingRequests,
  incrementPendingRequests,
  reportErrorMessage,
} from '../context/loaderManager';
import { useTimezoneStore } from '../store/timezoneStore';
import { normalizeApiDateTimes } from '../utils/timezoneUtils';

// Helper function to check if running in development mode
// Uses globalThis.import.meta.env.DEV for Jest compatibility
// Note: Does NOT use import.meta directly to avoid Jest parsing errors
const isDevelopment = () => {
  // globalThis.import.meta.env.DEV is set by Jest polyfill in jest.setup.js
  // In Vite, import.meta.env.DEV works natively
  return !!globalThis.import?.meta?.env?.DEV;
};

//Add token locally

export const initializeSessionFromRedirect = () => {
  const params = new URLSearchParams(window.location.search);

  const token = params.get('accessToken');
  const userId = params.get('svcManagerId');
  const deviceId = params.get('uuid');

  if (token) {
    sessionManager.setToken(token);
  }
  if (userId) {
    sessionManager.setUserId(userId);
  }
  if (deviceId) {
    sessionManager.setDeviceId(deviceId);
  }
};

export const setupRequestInterceptor = axiosInstance => {
  axiosInstance.interceptors.request.use(
    config => {
      const skipLoader = config.skipLoader;

      if (!skipLoader) {
        incrementPendingRequests();
      }

      // Add auth token to headers
      const token = sessionManager.getToken();
      if (sessionManager.isSessionValid()) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add device id to headers
      const deviceId = sessionManager.getDeviceId();
      if (deviceId) {
        config.headers['X-Device-Id'] = deviceId;
      }

      // Add timestamp
      config.headers['X-Request-Time'] = new Date().getTime();

      if (config.data && typeof config.data === 'object') {
        config.data = normalizeApiDateTimes(
          config.data,
          useTimezoneStore.getState().getActiveTimezone(),
          config.preserveUtcDateTimeFields
        );
      }

      return config;
    },
    error => {
      const skipLoader = error.config?.skipLoader;

      if (!skipLoader) {
        decrementPendingRequests();
      }

      console.error('Request Interceptor Error:', error);
      return Promise.reject(error);
    }
  );
};

export const setupResponseInterceptor = axiosInstance => {
  axiosInstance.interceptors.response.use(
    response => {
      const skipLoader = response.config.skipLoader;
      if (!skipLoader) {
        decrementPendingRequests();
      }

      const isDev = isDevelopment();
      if (isDev) {
        console.log('Response:', {
          status: response.status,
          statusText: response.statusText,
          url: response.config.url,
          data: response.data,
        });
      }

      return response;
    },
    async error => {
      const skipLoader = error.config?.skipLoader;
      if (!skipLoader) {
        decrementPendingRequests();
      }

      // Log error in dev (skip logging for suppressed errors)
      const skipErrorNotification = error.config?.skipErrorNotification;
      const isDev = isDevelopment();
      if (isDev && !skipErrorNotification) {
        console.error('Response Error:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          message: error.message,
          data: error.response?.data,
        });
      }

      // Handle network errors
      if (!error.response) {
        console.error('Network Error:', error.message);
        const errorMessage =
          'Network error. Please check your internet connection.';
        reportErrorMessage('Network Error', {
          message: errorMessage,
          originalError: error,
        });
        return Promise.reject({
          message: errorMessage,
          type: 'NETWORK_ERROR',
          originalError: error,
        });
      }

      // Handle 401 Unauthorized - logout user
      if (error.response.status === HTTP_STATUS.UNAUTHORIZED) {
        console.warn('Unauthorized access - logging out');
        authEvents.triggerLogoutOnce(() => {
          sessionManager.clearSession();
          window.location.href = '/error'; //TODO: remove hard redirect, add auth listener
        });

        const errorMessage = 'Session expired. Please login again.';
        reportErrorMessage('Unauthorized Access', {
          message: errorMessage,
          status: HTTP_STATUS.UNAUTHORIZED,
        });
        return Promise.reject({
          message: errorMessage,
          type: 'UNAUTHORIZED',
          status: HTTP_STATUS.UNAUTHORIZED,
          originalError: error,
        });
      }

      // Handle 403 Forbidden
      if (error.response.status === HTTP_STATUS.FORBIDDEN) {
        console.warn('Access Forbidden:', error.response.data);
        const errorMessage =
          'You do not have permission to access this resource.';
        reportErrorMessage('Access Forbidden', {
          message: errorMessage,
          status: HTTP_STATUS.FORBIDDEN,
          data: error.response.data,
        });
        return Promise.reject({
          message: errorMessage,
          type: 'FORBIDDEN',
          status: HTTP_STATUS.FORBIDDEN,
          data: error.response.data,
          originalError: error,
        });
      }

      // Handle 404 Not Found
      if (error.response.status === HTTP_STATUS.NOT_FOUND) {
        const skipErrorNotification = error.config?.skipErrorNotification;

        // Skip console warning and error notification if skipErrorNotification flag is set
        if (!skipErrorNotification) {
          console.warn('Resource Not Found:', error.config.url);
          const errorMessage = 'Requested resource not found.';
          reportErrorMessage('Resource Not Found', {
            message: errorMessage,
            status: HTTP_STATUS.NOT_FOUND,
          });
        }

        return Promise.reject({
          message: 'Requested resource not found.',
          type: 'NOT_FOUND',
          status: HTTP_STATUS.NOT_FOUND,
          originalError: error,
          suppressed: skipErrorNotification,
        });
      }

      // Handle 409 Conflict
      if (error.response.status === HTTP_STATUS.CONFLICT) {
        const errorMessage =
          error.response.data?.message || 'A conflict occurred.';
        reportErrorMessage('Conflict', {
          message: errorMessage,
          status: HTTP_STATUS.CONFLICT,
          data: error.response.data,
        });
        return Promise.reject({
          message: errorMessage,
          type: 'CONFLICT',
          status: HTTP_STATUS.CONFLICT,
          data: error.response.data,
          originalError: error,
        });
      }

      // Handle 422 Unprocessable Entity (Validation errors)
      if (error.response.status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
        const errorMessage =
          error.response.data?.message || 'Validation failed.';
        reportErrorMessage('Validation Error', {
          message: errorMessage,
          status: HTTP_STATUS.UNPROCESSABLE_ENTITY,
          errors: error.response.data?.errors || {},
        });
        return Promise.reject({
          message: errorMessage,
          type: 'VALIDATION_ERROR',
          status: HTTP_STATUS.UNPROCESSABLE_ENTITY,
          errors: error.response.data?.errors || {},
          originalError: error,
        });
      }

      // Handle 500 Internal Server Error
      if (error.response.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        console.error('Server Error:', error.response.data);
        const errorMessage = 'Internal server error. Please try again later.';
        reportErrorMessage('Server Error', {
          message: errorMessage,
          status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
        });
        return Promise.reject({
          message: errorMessage,
          type: 'SERVER_ERROR',
          status: HTTP_STATUS.INTERNAL_SERVER_ERROR,
          originalError: error,
        });
      }

      // Handle 503 Service Unavailable
      if (error.response.status === HTTP_STATUS.SERVICE_UNAVAILABLE) {
        const errorMessage =
          'Service temporarily unavailable. Please try again later.';
        reportErrorMessage('Service Unavailable', {
          message: errorMessage,
          status: HTTP_STATUS.SERVICE_UNAVAILABLE,
        });
        return Promise.reject({
          message: errorMessage,
          type: 'SERVICE_UNAVAILABLE',
          status: HTTP_STATUS.SERVICE_UNAVAILABLE,
          originalError: error,
        });
      }

      // Default error handling
      const errorMessage =
        error.response.data?.message || 'An unexpected error occurred.';
      reportErrorMessage('Error', {
        message: errorMessage,
        status: error.response.status,
        data: error.response.data,
      });
      return Promise.reject({
        message: errorMessage,
        type: 'UNKNOWN_ERROR',
        status: error.response.status,
        data: error.response.data,
        originalError: error,
      });
    }
  );
};

export const setupInterceptors = axiosInstance => {
  setupRequestInterceptor(axiosInstance);
  setupResponseInterceptor(axiosInstance);
};
